import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { mergeMap, of, share, switchMap, takeUntil, tap, timer } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as moment from 'moment';
import { KkmsDetailsService } from '../../../core/services/kkm-details.service';
import { Transaction, TransactionType } from '../../../core/models/transactions.model';
import { PageHeaderDropdownItem } from '../../../shared/page-header/page-header.model';
import { LanguageService } from '../../../core/services/language.service';
import { UsersService } from '../../users/user.service';
import { Report, SearchTicketResponse } from '../../../core/modules/kkms/kkms.types';
import { KkmsApiService } from '../../../core/modules/kkms/kkms-api.service';
import { IKkm, IRetailPlace } from '../../../core/modules/retail-places/retail-paces.types';
import { ReportApiService } from '../../../core/modules/report/report-api.service';
import { ReportIberica, ReportRequest } from '../../../core/modules/report/report.types';
import { RetailPlacesApiService } from '../../../core/modules/retail-places/retail-places-api.service';
import {
  QueryParamsFormData,
  TransactionFilterFormDateKeys,
  TransactionsFilterModel,
} from './transactions-filter-form/transactions-filter-form.types';
import {
  ReportTypeFilter,
  ReportTypeParam,
} from './transactions-filter-form/report-type-filters/report-type-filters.types';

@UntilDestroy()
@Component({
  selector: 'tax-kkm-details',
  templateUrl: './kkm-details.component.html',
  styleUrls: ['./kkm-details.component.sass'],
})
export class KkmDetailsComponent implements OnInit {
  readonly dropdownItems: PageHeaderDropdownItem[] = [{
    action: () => this.router.navigate(['settings'], {
      relativeTo: this.route,
    }),
    icon: 'edit',
    title: 'kkm.settings',
  }];

  transactionFilter?: TransactionsFilterModel;
  kkm?: IKkm;
  retailPlace?: IRetailPlace;
  transactions?: (Transaction | SearchTicketResponse)[];
  reports?: Report[];
  paginator = {
    index: 1,
    total: 0,
  };

  reportIberica?:ReportIberica;
  readonly userInfo$ = this.usersService.getCurrentUserInfo().pipe(share());
  readonly ReportTypeParam = ReportTypeParam;
  private readonly DATE_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

  constructor(
    private activatedRoute: ActivatedRoute,
    private kkmsApiService: KkmsApiService,
    private retailPlaceApiService: RetailPlacesApiService,
    private kkmsDetailsService: KkmsDetailsService,
    private languageService: LanguageService,
    private reportApiService: ReportApiService,
    private route: ActivatedRoute,
    private router: Router,
    private usersService: UsersService,
  ) { }

  ngOnInit(): void {
    let kkmId: number;
    let updateDate: number | undefined;
    this.activatedRoute.paramMap
      .pipe(
        tap((params: ParamMap) => {
          this.retailPlace = undefined;
          const id = params.get('id');
          if (id) {
            kkmId = parseInt(id, 10);
          }
        }),
        switchMap(() => timer(0, 12 * 1000)),
        mergeMap(() => this.kkmsDetailsService.getKkm(kkmId)),
        takeUntil(timer(12 * 1000 * 1000)),
        untilDestroyed(this),
      )
      .subscribe((kkm) => {
        this.kkm = kkm;
        if (this.kkm.lastShift && updateDate !== this.kkm.lastShift.updateDate) {
          updateDate = this.kkm.lastShift.updateDate;
        }

        if (!this.retailPlace) {
          this.getRetailPlace();
        }
      });
  }

  applyFilter(transactionFilter: TransactionsFilterModel): void {
    if (!this.kkm) return;
    this.transactionFilter = transactionFilter;
    const isIbericaReport = this.transactionFilter.reportType?.title === ReportTypeFilter.IBERICA_REPORT;
    this.paginator.index = 1;
    this.changeUrlQueryParams();
    isIbericaReport ? this.setReportIberica(this.kkm.id) : this.findTransaction(this.kkm.id);
  }

  setReportIberica(kkmId: number): void {
    if (!this.transactionFilter?.startDate || !this.transactionFilter?.endDate) {
      this.reportIberica = undefined;
      return;
    }

    this.userInfo$
      .pipe(
        switchMap((userInfo) => {
          const reportRequest: ReportRequest = {
            dateFrom: moment(this.transactionFilter?.startDate).format('DD.MM.YYYY'),
            dateTo: moment(this.transactionFilter?.endDate).format('DD.MM.YYYY'),
            reportType: 'CONSOLIDATED_REPORT',
            iinBin: userInfo.organisation.inn,
            dataDetailLevel: 'TOTALS',
            viewMode: 'WEBPAGE',
            filter: {
              type: 'BY_KKM',
              kkmList: [kkmId],
            },
          };
          return this.reportApiService.fetchReport(reportRequest);
        }),
        untilDestroyed(this),
      )
      .subscribe((report) => {
        this.reportIberica = report;
      });
  }

  openTicket(ticket: Transaction | SearchTicketResponse): void {
    if (!this.kkm) return;

    switch (ticket.transactionType) {
      case TransactionType.TICKET:
        this.router.navigate([`main/kkms/${this.kkm.id}/tickets/${ticket.id}?transactionDate=${ticket.transactionDate}`]);
        break;
      case TransactionType.X_REPORT:
        this.goToReport(ticket.id, ReportTypeFilter.X_REPORT, ticket.transactionDate);
        break;
      case TransactionType.Z_REPORT:
        this.goToReport(ticket.id, ReportTypeFilter.Z_REPORT, ticket.transactionDate);
        break;
      case TransactionType.MONEY_PLACEMENT:
      case TransactionType.CANCEL_TICKET:
      case TransactionType.CLOSE_SHIFT:
        break;
    }
  }

  openReport(report: Report): void {
    const reportType = this.transactionFilter?.reportType;
    if (!reportType) return;

    this.goToReport(report.id, reportType.title, report.transactionDate);
  }

  getPageData(pageIndex: number, kkmId: number): void {
    this.paginator.index = pageIndex;
    this.changeUrlQueryParams();
    this.getTransactions(kkmId);
  }

  private getReports(kkmId: number): void {
    this.kkmsDetailsService.getReports(
      kkmId,
      {
        reportType: this.transactionFilter?.reportType?.value ?? ReportTypeParam.X_REPORT,
        shiftNumber: this.transactionFilter?.shiftNumber,
        fromDate: this.getFormattedDate('startDate'),
        toDate: this.getFormattedDate('endDate'),
      },
    )
      .pipe(untilDestroyed(this))
      .subscribe((reports) => {
        this.reports = reports;
      });
  }

  private getTransactions(kkmId: number): void {
    this.kkmsDetailsService.getTransactions(
      kkmId,
      {
        page: this.paginator.index,
        shiftNumber: this.transactionFilter?.shiftNumber,
        fromDate: this.getFormattedDate('startDate'),
        toDate: this.getFormattedDate('endDate'),
      },
    )
      .pipe(untilDestroyed(this))
      .subscribe((response) => {
        this.transactions = response.transactions;
        this.paginator.total = (response.pagination.totalItems - response.pagination.pageSize) > 0
          ? Math.ceil(response.pagination.totalItems / response.pagination.pageSize)
          : 0;
      });
  }

  private findTransaction(kkmId: number): void {
    if (this.transactionFilter?.fiscalId) {
      this.kkmsApiService.fetchTransactionsByParams(kkmId, this.transactionFilter.fiscalId)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: (res) => {
            this.transactions = [res];
            this.paginator.total = 0;
          },
          error: () => {
            this.transactions = [];
            this.paginator.total = 0;
          },
        });
    } else if (!this.transactionFilter?.reportType) {
      this.getTransactions(kkmId);
      this.getReports(kkmId);
    } else {
      this.getReports(kkmId);
    }
  }

  private goToReport(id: string, type: ReportTypeFilter, date: string): void {
    if (!this.kkm) return;

    this.router.navigate([`main/kkms/${this.kkm.id}/${type}/${id}?reportDate=${date}`]);
  }

  private getRetailPlace(): void {
    this.languageService.languageChanges
      .pipe(
        switchMap(() => (this.kkm
          ? this.retailPlaceApiService.fetchRetailPlace(this.kkm.retailPlaceId)
          : of(undefined))),
        untilDestroyed(this),
      )
      .subscribe((retailPlace) => {
        this.retailPlace = retailPlace;
      });
  }

  private getFormattedDate(dateKey: TransactionFilterFormDateKeys): string | undefined {
    const date = this.transactionFilter?.[dateKey];
    if (!date) return undefined;

    const dateFormat = 'YYYY-MM-DDTHH:mm:ss';
    if (dateKey === 'startDate') return date.format(dateFormat);
    return date.endOf('day').format(dateFormat);
  }

  private changeUrlQueryParams(): void {
    this.router.navigate([], {
      queryParams: this.createQueryParams(),
      replaceUrl: true,
    });
  }

  private createQueryParams(): QueryParamsFormData | undefined {
    if (!this.transactionFilter) return;

    const {
      shiftNumber,
      reportType,
      startDate,
      endDate,
      fiscalId,
    } = this.transactionFilter;

    return {
      ...(!reportType && !fiscalId ? {
        page: this.paginator.index,
      } : {}),
      ...(reportType ? {
        reportType: reportType.value,
      } : {}),
      ...(shiftNumber && !fiscalId ? {
        shiftNumber,
      } : {}),
      ...(startDate && !fiscalId ? {
        startDate: startDate.format(this.DATE_FORMAT),
      } : {}),
      ...(endDate && !fiscalId ? {
        endDate: endDate.endOf('day').format(this.DATE_FORMAT),
      } : {}),
    };
  }
}
